import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";

import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";
import Layout from "../components/Layout";

import { FiTrash2, FiEye, FiSearch, FiClipboard } from "react-icons/fi";

export default function ManageContacts() {
  const navigate = useNavigate();

  // TABLE
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // DATE FILTER
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // VIEW MODAL
  const [viewContact, setViewContact] = useState(null);

  // DELETE MODAL
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // AUTH
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // DEBOUNCE SEARCH
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  // FETCH CONTACTS
  const fetchData = useCallback(async (pg, q, from, to) => {
    setLoading(true);

    try {
      const res = await API.get("/manage-contacts", {
        params: {
          page: pg,
          limit: 10,
          search: q,
          fromDate: from,
          toDate: to,
        },
      });

      setData(res.data?.data || []);
      setTotalPages(Number(res.data?.totalPages || 1));
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error(
        "Fetch contacts error:",
        error.response?.data || error.message
      );

      toasts.error(error.response?.data?.error || "Failed to load Contacts");

      setData([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // FETCH WHEN FILTERS CHANGE
  useEffect(() => {
    fetchData(page, debouncedSearch, fromDate, toDate);
  }, [page, debouncedSearch, fromDate, toDate, fetchData]);

  // SEARCH CHANGE
  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearch(value);

    if (page !== 1) {
      setPage(1);
    }
  };

  // CLEAR SEARCH
  const handleClearSearch = () => {
    setSearch("");

    if (page !== 1) {
      setPage(1);
    }
  };

  // DATE SEARCH
  const handleDateSearch = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toasts.warning("From Date cannot be greater than To Date");
      return;
    }

    if (page !== 1) {
      setPage(1);
    }
  };

  // CLEAR DATE FILTER
  const handleClearDates = () => {
    setFromDate("");
    setToDate("");

    if (page !== 1) {
      setPage(1);
    }
  };

  // VIEW CONTACT
  const handleView = (row) => {
    setViewContact(row);
  };

  // CLOSE VIEW MODAL
  const closeViewModal = () => {
    setViewContact(null);
  };

  // OPEN DELETE MODAL
  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // CLOSE DELETE MODAL
  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // DELETE CONTACT
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/manage-contacts/${delModal.cont_Id}`);

      toasts.success("Contact deleted successfully");

      setDelModal(null);

      const remainingRecords = Math.max(0, total - 1);

      const newTotalPages = Math.max(1, Math.ceil(remainingRecords / 10));

      if (page > 1 && page > newTotalPages) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        await fetchData(page, debouncedSearch, fromDate, toDate);
      }
    } catch (error) {
      console.error(
        "Delete contact error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };
 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };
  // RETURN
  return (
    <Layout activePage="Contacts">
      <main className="dashboard-main">
        <div className="season-card">
          {/* HEADER */}
          <div className="table-card-head">
            {/* TITLE */}
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Contacts</span>

              <span className="rec-count ms-1">{total} contacts</span>
            </h3>

            {/* SEARCH */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                className="search-input"
                placeholder="Search Contact..."
                value={search}
                onChange={handleSearchChange}
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={handleClearSearch}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* DATE FILTER */}
          <div className="table-card-head">
            <div className="row align-items-center g-2">
              {/* FROM DATE */}
              <div className="col-auto">
                <div className="d-flex gap-2">
                  <label className="field-clabel mb-0">From</label>

                  <input
                    type="date"
                    className="season-input form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
              </div>

              {/* TO DATE */}
              <div className="col-auto">
                <div className="d-flex gap-2">
                  <label className="field-clabel mb-0">To</label>

                  <input
                    type="date"
                    className="season-input form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>

              {/* SEARCH BUTTON */}
              <div className="col-auto">
                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleDateSearch}
                >
                  <FiSearch size={15} />

                  <span className="ms-1">Search</span>
                </button>
              </div>

              {/* CLEAR BUTTON */}
              {(fromDate || toDate) && (
                <div className="col-auto">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={handleClearDates}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="um-th text-nowrap">#</th>

                  <th className="um-th text-nowrap text-center">Name</th>

                  <th className="um-th text-nowrap text-center">Email ID</th>

                  <th className="um-th text-nowrap text-center">Contact No.</th>

                  <th className="um-th text-nowrap text-center">Date</th>

                  <th className="um-th text-nowrap text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="um-td text-center">
                      Loading…
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="empty-table-cell text-center py-5"
                    >
                      {debouncedSearch || fromDate || toDate
                        ? "No matching Contacts found"
                        : "No Contacts found"}
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => (
                    <tr key={row.cont_Id} className="um-row">
                      {/* NUMBER */}
                      <td className="um-td user-number text-center">
                        {(page - 1) * 10 + i + 1}
                      </td>

                      {/* NAME */}
                      <td className="um-td text-center">
                        <span className="season-name-chip">
                          {row.cont_name || "-"}
                        </span>
                      </td>

                      {/* EMAIL */}
                      <td className="um-td text-center">
                        {row.cont_email || "-"}
                      </td>

                      {/* CONTACT */}
                      <td className="um-td text-center">
                        {row.cont_telno || "-"}
                      </td>

                      {/* DATE */}
                      <td className="um-td text-center">
                        {row.cont_createddate
                          ? new Date(row.cont_createddate).toLocaleDateString(
                            "en-GB"
                          )
                          : "-"}
                      </td>

                      {/* ACTION */}
                      <td className="um-td text-center season-action-td">
                        <div className="action-buttons d-flex justify-content-center align-items-center gap-2">
                          {/* VIEW */}
                          <button
                            type="button"
                            onClick={() => handleView(row)}
                            className="action-btn action-view btn btn-sm"
                            title="View"
                          >
                            <FiEye size={15} />
                          </button>

                          {/* DELETE */}
                          <button
                            type="button"
                            onClick={() => openDeleteModal(row)}
                            className="action-btn action-delete btn btn-sm"
                            title="Delete"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* PAGINATION */}
            <Pagination
  page={page}
  totalPages={totalPages}
  total={total}
  limit={10}
  loading={loading}
  onPrevious={handlePrevious}
  onNext={handleNext}
/>
          </div>
        </div>

        {/* VIEW CONTACT MODAL */}
        {viewContact && (
          <div className="um-overlay" onClick={closeViewModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}

              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">Contact Details</h2>

                  <p className="form-card-sub mb-0">View contact information</p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeViewModal}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}

              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* NAME */}

                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Name
                      </div>

                      <div className="fw-bold">
                        {viewContact.cont_name || "—"}
                      </div>
                    </div>
                  </div>

                  {/* EMAIL */}

                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Email ID
                      </div>

                      <div className="fw-bold">
                        {viewContact.cont_email || "—"}
                      </div>
                    </div>
                  </div>

                  {/* CONTACT */}

                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Contact No.
                      </div>

                      <div className="fw-bold">
                        {viewContact.cont_telno || "—"}
                      </div>
                    </div>
                  </div>

                  {/* DATE */}

                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3 h-100">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Date
                      </div>

                      <div className="fw-bold">
                        {viewContact.cont_createddate
                          ? new Date(
                            viewContact.cont_createddate
                          ).toLocaleDateString("en-GB")
                          : "—"}
                      </div>
                    </div>
                  </div>

                  {/* MESSAGE */}

                  <div className="col-md-12">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Message
                      </div>

                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                          wordBreak: "break-word",
                        }}
                      >
                        { viewContact.cont_comment || "—" }
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeViewModal}
                >
                  ✕ Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {delModal && (
          <div className="um-overlay" onClick={closeDeleteModal}>
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                <h2 className="delete-title">Delete Contact?</h2>

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    "{delModal.cont_name}"
                  </span>
                  ?
                  <br />
                  This will permanently remove this contact.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                <div className="delete-actions">
                  {/* CANCEL */}
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={closeDeleteModal}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  {/* DELETE */}
                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TOAST */}

      </main>
    </Layout>
  );
}
